import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBreadcrumbs, useUser } from './Hooks';
import API, { CDN_URL } from './API';
import { Icon, Sidebar } from 'react-frontier';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { bindClose, bindToggle } from './SiteUtil';
import UserAccess from './UserAccess';
import classNames from 'classnames';

import './style/site.scss';

interface SiteContainerProps{
	outlet: JSX.Element,
}

var SiteContainer = (props: SiteContainerProps)=>{
	var { user, logout, hasAccess } = useUser();
	var { breadcrumbs, sidebar } = useBreadcrumbs();
	var [drawer, setDrawer] = useState<boolean>(false);
	var [counts, setCounts] = useState<{ prospects_recent: number, convertir: number }>(null);
	var location = useLocation();

	useEffect(()=>{
		setDrawer(false);
	}, [location.pathname]);

	useEffect(()=>{
		if(!hasAccess(UserAccess.PROSPECTS.VIEW_PROSPECTS)) return;
		API.getSidebarCounts().then(res=>{
			if(!res.error) setCounts(res.data);
		}).catch(()=>{});
	}, [location.pathname]);

	var isActive = (to: string, exact?: boolean)=>{
		if(exact || to==='/') return location.pathname===to;
		return location.pathname.startsWith(to);
	}

	var MenuHeader = <Link to='/' className="brand">
		<img src={`${CDN_URL}/logo/LogoBH_Large.webp`} alt="Arema CRM" style={{ height: 22, objectFit: 'contain' }} />
	</Link>

	var MenuItems = useMemo(()=>{
		if(sidebar){
			return <>
				{sidebar}
				<div className="filler"></div>
				{!!user && <Sidebar.Item text={`${user.first_name} ${user.last_name}`} iconName='user' className='extra' />}
				<Sidebar.Item text='Cerrar sesión' iconName='sign-out' className='extra' onClick={logout} />
			</>
		}
		return <>
			<div className="divider">General</div>
			{hasAccess(UserAccess.PROSPECTS.VIEW_PROSPECTS) && (
				<Sidebar.Item as={Link} to='/' text='Prospectos' iconName='user-plus' active={isActive('/', true)} style={{ position: 'relative' }}>
					{!!counts && counts.prospects_recent > 0 && <span className='sidebar-badge'>{counts.prospects_recent}</span>}
				</Sidebar.Item>
			)}
			{hasAccess(UserAccess.PROMOTERS.VIEW_PROMOTERS) && (
				<Sidebar.Item as={Link} to='/promoters' text='Promotores' iconName='user-tie' active={isActive('/promoters')} />
			)}
			{hasAccess([UserAccess.PROSPECTS.VIEW_PROSPECTS, UserAccess.PROMOTERS.VIEW_PROMOTERS]) && (
				<Sidebar.Item as={Link} to='/inactive' text='Inactivos' iconName='user-slash' active={isActive('/inactive')} />
			)}
			{hasAccess(UserAccess.PROSPECTS.VIEW_PROSPECTS) && (
				<Sidebar.Item as={Link} to='/convert' text='Convertir' iconName='right-left' active={isActive('/convert')} style={{ position: 'relative' }}>
					{!!counts && counts.convertir > 0 && <span className='sidebar-badge'>{counts.convertir}</span>}
				</Sidebar.Item>
			)}
			{hasAccess(UserAccess.PROSPECTS.VIEW_PROSPECTS) && (
				<Sidebar.Item as={Link} to='/postponed' text='Aplazados' iconName='clock' active={isActive('/postponed')} />
			)}
			{hasAccess(UserAccess.PROSPECTS.VIEW_PROSPECTS) && (
				<Sidebar.Item as={Link} to='/disqualified' text='Descalificados' iconName='ban' active={isActive('/disqualified')} />
			)}
			<div className="divider">Administración</div>
			{hasAccess([UserAccess.PROSPECTS.VIEW_PROSPECTS, UserAccess.PROMOTERS.VIEW_PROMOTERS]) && (
				<Sidebar.Item as={Link} to='/activities' text='Actividades' iconName='tasks' active={isActive('/activities')} />
			)}
			{hasAccess([UserAccess.PROMOTERS.VIEW_PROMOTERS, UserAccess.FORMS.VIEW_FORMS]) && (
				<Sidebar.Item as={Link} to='/responses' text='Encuestas' iconName='clipboard-check' active={isActive('/responses')} />
			)}
			<Sidebar.Item as={Link} to='/externals' text='Eventos externos' iconName='file-excel' active={isActive('/externals')} />
			{hasAccess([UserAccess.PROSPECTS.VIEW_PROSPECTS, UserAccess.PROMOTERS.VIEW_PROMOTERS]) && (
				<>
					<div className="divider">Reportes</div>
					<Sidebar.Item as={Link} to='/reports/dates' text='Reporte de fechas' iconName='calendar' active={isActive('/reports/dates')} />
					<Sidebar.Item as={Link} to='/reports/promoters' text='Reporte promotores' iconName='user-tie' active={isActive('/reports/promoters')} />
					<Sidebar.Item as={Link} to='/reports/surveys' text='Reporte de encuestas' iconName='chart-pie' active={isActive('/reports/surveys')} />
					<Sidebar.Item as={Link} to='/reports/states' text='Mapa de eventos' iconName='map' active={isActive('/reports/states')} />
				</>
			)}
			<div className="divider">Configuración</div>
			{hasAccess(UserAccess.FORMS.VIEW_FORMS) && (
				<Sidebar.Item as={Link} to='/forms' text='Formularios' iconName='file-alt' active={isActive('/forms')} />
			)}
			{hasAccess(UserAccess.FORMS.VIEW_FORMS) && (
				<Sidebar.Item as={Link} to='/surveys' text='Preguntas de encuesta' iconName='clipboard-question' active={isActive('/surveys')} />
			)}
			{hasAccess(UserAccess.ACTIVITIES.VIEW_ACTIVITY_TYPES) && (
				<Sidebar.Item as={Link} to='/types' text='Tipos de actividad' iconName='list' active={isActive('/types')} />
			)}
			{hasAccess(UserAccess.USERS.VIEW_USERS) && (
				<Sidebar.Item as={Link} to='/users' text='Usuarios' iconName='user' active={isActive('/users')} />
			)}
			<div className="filler"></div>
			{!!user && <Sidebar.Item text={`${user.first_name} ${user.last_name}`} iconName='user' className='extra' />}
			<Sidebar.Item text='Cerrar sesión' iconName='sign-out' className='extra' onClick={logout} />
		</>
	}, [location, sidebar, user, counts]);

	var Navbar = <div className="navbar">
		{breadcrumbs && breadcrumbs.length>1 ? (
			<Link className='nav' to={breadcrumbs[breadcrumbs.length-2].url}>
				<Icon name='arrow-left' />
			</Link>
		) : (
			<div className="nav disabled">
				<Icon name='arrow-left' />
			</div>
		)}
		<div className="items">
			{!breadcrumbs || breadcrumbs.length===0 ? (
				<div className="item active">
					<Icon name='user-plus' />
					Prospectos
				</div>
			) : (
				breadcrumbs.map((a, i, arr)=>(
					<React.Fragment key={`brdcrb-${a.url}`}>
						<Link to={a.url} className={classNames("item", {
							active: i>=(arr.length-1)
						})}>
							{!!a.iconName && <Icon name={a.iconName} />}
							{a.text}
						</Link>
						{!!(i<arr.length-1) && (
							<div className='divider'>/</div>
						)}
					</React.Fragment>
				))
			)}
		</div>
		<div className="drawer" onClick={bindToggle(drawer, setDrawer)}>
			<Icon name='bars' />
		</div>
	</div>

	if(!user) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f0f0f0' }}>
		<Icon name='spinner' loading />
	</div>

	return <Sidebar className="ar">
		<Sidebar.Menu className="main-menu" header={MenuHeader}>
			{MenuItems}
		</Sidebar.Menu>
		<Sidebar.Contents header={Navbar}>
			<div className="page-contents" id="siteContents">
				{props.outlet}
			</div>
		</Sidebar.Contents>
		<TransitionGroup component={React.Fragment}>
			{!!drawer && (
				<CSSTransition enter addEndListener={null} timeout={{ appear: 0, exit: 500 }}>
					<div className="overlay" onClick={bindClose(setDrawer, false)}></div>
				</CSSTransition>
			)}
		</TransitionGroup>
		<TransitionGroup component={React.Fragment}>
			{!!drawer && (
				<CSSTransition enter addEndListener={null} timeout={{ appear: 0, exit: 500 }}>
					<Sidebar.Menu className="main-menu drawer-menu" header={MenuHeader}>
						{MenuItems}
					</Sidebar.Menu>
				</CSSTransition>
			)}
		</TransitionGroup>
	</Sidebar>
}

export default SiteContainer;
